import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AuthProvider } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { Nav } from "./components/Nav";
import { AdminLayout } from "./components/AdminLayout";
import { Footer } from "./components/Footer";
import { SessionExpiryWarning } from "./components/SessionExpiryWarning";
import { AiConsentPrompt } from "./components/AiConsentPrompt";
import { ScrollToTop } from "./components/ScrollToTop";
import "./styles/index.css";

// Page stubs (to be implemented in Phase 1+)
import { LoginPage } from "./pages/Login";
import { RegisterPage } from "./pages/Register";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { AccountPage } from "./pages/Account";
import { RegisterInstructorPage } from "./pages/RegisterInstructor";
import { LandingPage } from "./pages/Landing";
import { NotFoundPage } from "./pages/NotFound";
import { HowItWorksPage } from "./pages/info/HowItWorks";
import { MasteryPage } from "./pages/info/Mastery";
import { ForEducatorsPage } from "./pages/info/ForEducators";
import { CapsAlignmentPage } from "./pages/info/CapsAlignment";
import { PrivacyPage } from "./pages/info/Privacy";
import { ContactPage } from "./pages/info/Contact";
import { TermsPage } from "./pages/info/Terms";
import { CurriculaPage } from "./pages/learner/Curricula";
import { BrowsePage } from "./pages/learner/Browse";
import { RoadmapPage } from "./pages/learner/Roadmap";
import { LessonPage } from "./pages/learner/Lesson";
import { DialogPage } from "./pages/learner/Dialog";
import { QuizPage } from "./pages/learner/Quiz";
import { SummaryPage } from "./pages/learner/Summary";
import { ProgressPage } from "./pages/learner/Progress";
import { TutorDashboardPage } from "./pages/tutor/Dashboard";
import { TutorRosterPage } from "./pages/tutor/Roster";
import { MyCoursesPage } from "./pages/tutor/MyCourses";
import { CourseEditorPage } from "./pages/tutor/CourseEditor";
import { TutorLearnerDetailPage } from "./pages/tutor/LearnerDetail";
import { MarkReviewsPage } from "./pages/tutor/MarkReviews";
import { AdminOverviewPage } from "./pages/admin/Overview";
import { AdminUsersPage } from "./pages/admin/Users";
import { AdminPendingInstructorsPage } from "./pages/admin/PendingInstructors";
import { AdminLearnersPage } from "./pages/admin/Learners";
import { AdminCurriculaPage } from "./pages/admin/Curricula";
import { AdminReportsPage } from "./pages/admin/Reports";
import { GraderComparisonPage } from "./pages/admin/GraderComparison";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          {/* Inside the router (it reads the location), outside the routes so
              it survives the swap and fires on every navigation. */}
          <ScrollToTop />
          <div className="app-shell">
          <a className="skip-link" href="#main-content">Skip to main content</a>
          <Nav />
          <SessionExpiryWarning />
          <AiConsentPrompt />
          <main id="main-content" className="app-main" tabIndex={-1}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/teach" element={<RegisterInstructorPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Public info pages (footer destinations) */}
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/mastery" element={<MasteryPage />} />
            <Route path="/for-educators" element={<ForEducatorsPage />} />
            <Route path="/caps" element={<CapsAlignmentPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Any signed-in user, whatever their role */}
            <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />

            {/* Learner */}
            <Route path="/learn" element={<ProtectedRoute roles={["learner"]}><CurriculaPage /></ProtectedRoute>} />
            <Route path="/browse" element={<ProtectedRoute roles={["learner"]}><BrowsePage /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute roles={["learner"]}><ProgressPage /></ProtectedRoute>} />
            <Route path="/learn/:cid" element={<ProtectedRoute roles={["learner"]}><RoadmapPage /></ProtectedRoute>} />
            <Route path="/learn/:cid/lesson/:lid" element={<ProtectedRoute roles={["learner"]}><LessonPage /></ProtectedRoute>} />
            <Route path="/learn/:cid/dialog/:lid" element={<ProtectedRoute roles={["learner"]}><DialogPage /></ProtectedRoute>} />
            <Route path="/learn/:cid/quiz/:lid" element={<ProtectedRoute roles={["learner"]}><QuizPage /></ProtectedRoute>} />
            <Route path="/learn/:cid/summary/:sid" element={<ProtectedRoute roles={["learner"]}><SummaryPage /></ProtectedRoute>} />

            {/* Tutor */}
            <Route path="/tutor" element={<ProtectedRoute roles={["instructor"]}><TutorDashboardPage /></ProtectedRoute>} />
            <Route path="/tutor/learners" element={<ProtectedRoute roles={["instructor"]}><TutorRosterPage /></ProtectedRoute>} />
            <Route path="/tutor/courses" element={<ProtectedRoute roles={["instructor"]}><MyCoursesPage /></ProtectedRoute>} />
            <Route path="/tutor/courses/:cid" element={<ProtectedRoute roles={["instructor"]}><CourseEditorPage /></ProtectedRoute>} />
            <Route path="/tutor/learners/:id" element={<ProtectedRoute roles={["instructor"]}><TutorLearnerDetailPage /></ProtectedRoute>} />
            <Route path="/tutor/marks" element={<ProtectedRoute roles={["instructor"]}><MarkReviewsPage /></ProtectedRoute>} />

            {/* Admin. One guarded layout route rather than seven guards: the
                sidebar is part of every admin screen, so it lives above them. */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="instructors" element={<AdminPendingInstructorsPage />} />
              <Route path="learners" element={<AdminLearnersPage />} />
              <Route path="curricula" element={<AdminCurriculaPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="grader-comparison" element={<GraderComparisonPage />} />
            </Route>

            {/* Keep mistyped or expired links recoverable instead of rendering
                an empty application shell. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </main>
          <Footer />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
